/**
 * Orders outbound lifecycle — portal sanitize → complete → send parsed payload → ERP ack.
 *
 * Kept separate from the line/importer contract so a later lift into `@daka/core`
 * HOW (e.g. defineOutboundLifecycle) stays honest. Not a module.
 */
import type { MessageKey } from '@daka/core';

import { ORDER_MESSAGES } from './messages';

/**
 * draft → editable (staging import and/or order lines) · completed → ready to send
 * (read-only until Revert) · submitted → handed to ERP (locked) · confirmed → ERP
 * accepted (locked) · rejected → ERP refused (read-only until Revert).
 *
 * One order document owns the lifecycle; spreadsheet staging is HOW while
 * `stagingImportId` is set, not a second document type.
 */
export const ORDER_STATUSES = ['draft', 'completed', 'submitted', 'confirmed', 'rejected'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS = {
   DRAFT: 'draft',
   COMPLETED: 'completed',
   SUBMITTED: 'submitted',
   CONFIRMED: 'confirmed',
   REJECTED: 'rejected'
} as const satisfies Record<string, OrderStatus>;

/** Kit StatusBadge tones — one map every status chip uses. */
export type OrderStatusTone = 'neutral' | 'info' | 'warning' | 'success' | 'danger';

export const ORDER_STATUS_TONE = {
   draft: 'neutral',
   completed: 'info',
   submitted: 'warning',
   confirmed: 'success',
   rejected: 'danger'
} as const satisfies Record<OrderStatus, OrderStatusTone>;

export const ORDER_STATUS_LABEL = {
   draft: ORDER_MESSAGES.statusDraft,
   completed: ORDER_MESSAGES.statusCompleted,
   submitted: ORDER_MESSAGES.statusSubmitted,
   confirmed: ORDER_MESSAGES.statusConfirmed,
   rejected: ORDER_MESSAGES.statusRejected
} as const satisfies Record<OrderStatus, MessageKey>;

/** Locked while the ERP has it or accepted it — no edits, delete, or revert. */
export function isOrderLocked(status: OrderStatus): boolean {
   return status === ORDER_STATUS.SUBMITTED || status === ORDER_STATUS.CONFIRMED;
}

/** Line editors only while draft; completed/rejected need Revert first. */
export function isOrderEditable(status: OrderStatus): boolean {
   return status === ORDER_STATUS.DRAFT;
}

/** Revert to draft from completed (before send) or rejected (after ERP refuse). */
export function canRevertOrder(status: OrderStatus): boolean {
   return status === ORDER_STATUS.COMPLETED || status === ORDER_STATUS.REJECTED;
}
